import { FAST_PAY_TABS_MAN_SLUG_MAP } from '@/constants/FAST_PAY_TABS_MAN_SLUG_MAP';
import { MAIN_697_SLUG_MAP } from '@/constants/MAIN_697_SLUG_MAP';
import { SKINNY_REGULAR_ON_BED_SLUG_MAP } from '@/constants/SKINNY_REGULAR_ON_BED_SLUG_MAP';
import { TRANS_SLUG_MAP } from '@/constants/TRANS_SLUG_MAP';
import { V6_SEXY_SLUG_MAP } from '@/constants/V6_SEXY_SLUG_MAP';
import { V6_SLUG_MAP } from '@/constants/V6_SLUG_MAP';
import { WebCampaignType } from '@/global';
import { webCampaign } from '@/modules/webCampaign';

type UserSelectionsType = {
  ageGeneration?: string | null;
  hairColor?: string | null;
  bodyShape?: string | null;
  ethnicity?: string | null;
  his_type?: string | null;
  gender?: 'male' | 'female' | null;
  perfectLook?: string | null;
};

type SlugsVariantType = NonNullable<WebCampaignType['paywallImagesVariant']>;

const botSlugsMap: Record<SlugsVariantType, Record<string, string>> = {
  v6: V6_SLUG_MAP,
  'v6-sexy': V6_SEXY_SLUG_MAP,
  skinny_regular_on_bed: SKINNY_REGULAR_ON_BED_SLUG_MAP,
  'fast-pay-tabs-man': FAST_PAY_TABS_MAN_SLUG_MAP,
  'main-697': MAIN_697_SLUG_MAP,
  'one-night-stand': {},
  'one-night-stand-constructor': {
    ...V6_SEXY_SLUG_MAP,
    ...FAST_PAY_TABS_MAN_SLUG_MAP,
  },
  multigender: {
    ...V6_SLUG_MAP,
    ...FAST_PAY_TABS_MAN_SLUG_MAP,
  },
  transsexual: TRANS_SLUG_MAP,
};

export const getBotSlug = (userSelections: UserSelectionsType) => {
  const botSluggerFactory = new BotSluggerFactory();
  const botSlugger = botSluggerFactory.createSlugger();

  if (botSlugger) {
    return botSlugger.findBotSlugByUserSelections(userSelections);
  }

  return null;
};

class BotSluggerFactory {
  createSlugger(): BotSlugger | null {
    // if (webCampaign.getKey() === 'g34bjedMG9_19_fm') {
    //   return new G34bjedMG9_19_fmSlugger();
    // }
    if (webCampaign.setup.paywallNotes?.includes('MAIN-697')) {
      return new Main697Slugger();
    }
    if (webCampaign.setup.paywallImagesVariant === 'v6-sexy') {
      return new V6SexySlugger();
    }
    if (webCampaign.setup.paywallImagesVariant === 'skinny_regular_on_bed') {
      return new SkinnyRegularOnBedSlugger();
    }
    if (webCampaign.setup.paywallImagesVariant === 'fast-pay-tabs-man') {
      return new FastPayTabsManSlugger();
    }
    if (webCampaign.setup.paywallImagesVariant === 'v6') {
      return new V6Slugger();
    }
    if (webCampaign.setup.paywallImagesVariant === 'one-night-stand') {
      return new OneNightStandSlugger();
    }
    if (
      webCampaign.setup.paywallImagesVariant === 'one-night-stand-constructor'
    ) {
      return new OneNightStandConstructorSlugger();
    }
    if (webCampaign.setup.paywallImagesVariant === 'multigender') {
      return new MultigenderSlugger();
    }
    if (webCampaign.setup.paywallImagesVariant === 'transsexual') {
      return new TranssexualSlugger();
    }
    return null;
  }
}

abstract class BotSlugger {
  abstract findBotSlugByUserSelections(_: UserSelectionsType): string | null;
}

class V6Slugger extends BotSlugger {
  private _slugVariant: SlugsVariantType = 'v6';

  constructor() {
    super();
  }

  findBotSlugByUserSelections(
    userSelections: UserSelectionsType,
  ): string | null {
    if (
      !userSelections.ageGeneration ||
      !userSelections.hairColor ||
      !userSelections.bodyShape
    ) {
      return null;
    }

    const botSlugs = botSlugsMap[this._slugVariant];
    const userSelectsString = `${userSelections.ageGeneration}_${userSelections.hairColor}_${userSelections.bodyShape}`;

    return userSelectsString in botSlugs ? botSlugs[userSelectsString] : null;
  }
}

class V6SexySlugger extends BotSlugger {
  private _slugVariant: SlugsVariantType = 'v6-sexy';

  constructor() {
    super();
  }

  findBotSlugByUserSelections(
    userSelections: UserSelectionsType,
  ): string | null {
    if (
      !userSelections.ageGeneration ||
      !userSelections.hairColor ||
      !userSelections.bodyShape
    ) {
      return null;
    }

    const botSlugs = botSlugsMap[this._slugVariant];
    const userSelectsString = `${userSelections.ageGeneration}_${userSelections.hairColor}_${userSelections.bodyShape}`;

    return userSelectsString in botSlugs ? botSlugs[userSelectsString] : null;
  }
}

class SkinnyRegularOnBedSlugger extends BotSlugger {
  private _slugVariant: SlugsVariantType = 'skinny_regular_on_bed';

  constructor() {
    super();
  }

  findBotSlugByUserSelections(
    userSelections: UserSelectionsType,
  ): string | null {
    if (
      !userSelections.ageGeneration ||
      !userSelections.hairColor ||
      !userSelections.bodyShape
    ) {
      return null;
    }

    const botSlugs = botSlugsMap[this._slugVariant];
    const userSelectsString = `${userSelections.ageGeneration}_${userSelections.hairColor}_${userSelections.bodyShape}`;

    return userSelectsString in botSlugs ? botSlugs[userSelectsString] : null;
  }
}

class FastPayTabsManSlugger extends BotSlugger {
  private _slugVariant: SlugsVariantType = 'fast-pay-tabs-man';

  constructor() {
    super();
  }

  findBotSlugByUserSelections(
    userSelections: UserSelectionsType,
  ): string | null {
    if (
      !userSelections.ageGeneration ||
      !userSelections.ethnicity ||
      !userSelections.bodyShape
    ) {
      return null;
    }

    const botSlugs = botSlugsMap[this._slugVariant];
    const userSelectsString = `${userSelections.ageGeneration}_${userSelections.ethnicity}_${userSelections.bodyShape}`;

    return userSelectsString in botSlugs ? botSlugs[userSelectsString] : null;
  }
}

class Main697Slugger extends BotSlugger {
  findBotSlugByUserSelections(
    userSelections: UserSelectionsType,
  ): string | null {
    if (!userSelections.his_type) return null;
    return MAIN_697_SLUG_MAP[userSelections.his_type] || null;
  }
}

class OneNightStandSlugger extends BotSlugger {
  findBotSlugByUserSelections(userSelections: UserSelectionsType): string {
    return 'half-mile-away-from-you';
  }
}

class MultigenderSlugger extends BotSlugger {
  findBotSlugByUserSelections(
    userSelections: UserSelectionsType,
  ): string | null {
    if (!userSelections.gender) {
      return null;
    }

    if (userSelections.gender === 'female') {
      if (
        !userSelections.ageGeneration ||
        !userSelections.hairColor ||
        !userSelections.bodyShape
      ) {
        return null;
      }

      const botSlugs = botSlugsMap['v6'];
      const userSelectsString = `${userSelections.ageGeneration}_${userSelections.hairColor}_${userSelections.bodyShape}`;

      return userSelectsString in botSlugs ? botSlugs[userSelectsString] : null;
    }

    if (userSelections.gender === 'male') {
      if (
        !userSelections.ageGeneration ||
        !userSelections.ethnicity ||
        !userSelections.bodyShape
      ) {
        return null;
      }

      const botSlugs = botSlugsMap['fast-pay-tabs-man'];
      const userSelectsString = `${userSelections.ageGeneration}_${userSelections.ethnicity}_${userSelections.bodyShape}`;

      return userSelectsString in botSlugs ? botSlugs[userSelectsString] : null;
    }

    return null;
  }
}

class OneNightStandConstructorSlugger extends BotSlugger {
  findBotSlugByUserSelections(
    userSelections: UserSelectionsType,
  ): string | null {
    if (!userSelections.gender) {
      return null;
    }

    if (userSelections.gender === 'female') {
      if (
        !userSelections.ageGeneration ||
        !userSelections.hairColor ||
        !userSelections.bodyShape
      ) {
        return null;
      }

      const botSlugs = botSlugsMap['v6-sexy'];
      const userSelectsString = `${userSelections.ageGeneration}_${userSelections.hairColor}_${userSelections.bodyShape}`;

      return userSelectsString in botSlugs ? botSlugs[userSelectsString] : null;
    }

    if (userSelections.gender === 'male') {
      if (
        !userSelections.ageGeneration ||
        !userSelections.ethnicity ||
        !userSelections.bodyShape
      ) {
        return null;
      }

      const botSlugs = botSlugsMap['fast-pay-tabs-man'];
      const userSelectsString = `${userSelections.ageGeneration}_${userSelections.ethnicity}_${userSelections.bodyShape}`;

      return userSelectsString in botSlugs ? botSlugs[userSelectsString] : null;
    }

    return null;
  }
}

class TranssexualSlugger extends BotSlugger {
  private _slugVariant: SlugsVariantType = 'transsexual';

  constructor() {
    super();
  }

  findBotSlugByUserSelections(
    userSelections: UserSelectionsType,
  ): string | null {
    if (!userSelections.perfectLook) {
      return null;
    }

    const botSlugs = botSlugsMap[this._slugVariant];
    return userSelections.perfectLook in botSlugs
      ? botSlugs[userSelections.perfectLook]
      : null;
  }
}
